/**
 * Preview modal for shared files.
 *
 * Flow:
 *   1. Modal opens with the file's metadata.
 *   2. If `has_password`, the modal first shows a password prompt with an
 *      inline "Şifreyi görüntüle" button (visible to admins & recipients).
 *   3. On correct password, the modal fetches the file as a blob and renders
 *      a preview (image / pdf / text) OR a "Bu dosya türü için önizleme yok
 *      — İndir" fallback.
 *   4. A "İndir" button lets the user save the file. For protected files the
 *      password is passed as a query param so the backend can verify it.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Download, X, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { sharedFilesApi } from "../../lib/copApi";
import { useAuthStore } from "../../store/authStore";
import { previewKindFor, iconFor, formatSize } from "./sharedFilesHelpers";

export default function SharedFilePreviewModal({ file, onClose, filesApi = sharedFilesApi }) {
  const { user } = useAuthStore();
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [textBody, setTextBody] = useState("");
  const [loadingBlob, setLoadingBlob] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [revealedPw, setRevealedPw] = useState("");

  useEffect(() => {
    if (!file) return;
    setPassword("");
    setUnlocked(!file.has_password);
    setBlobUrl(null);
    setTextBody("");
    setRevealedPw("");
    setShowPw(false);
  }, [file]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  useEffect(() => {
    if (!file || !unlocked) return;
    const kind = previewKindFor(file.mime_type, file.size);
    if (kind === "none") return;
    setLoadingBlob(true);
    (async () => {
      try {
        const res = await api.get(filesApi.downloadUrl(file.id, password), {
          responseType: "blob",
        });
        if (kind === "text") {
          const txt = await res.data.text();
          setTextBody(txt);
        } else {
          // Force blob MIME type so Chrome's built-in PDF viewer picks it
          // up when the iframe loads the blob URL — axios sometimes strips
          // the type when responseType is "blob".
          const typed = new Blob([res.data], {
            type: file.mime_type || "application/octet-stream",
          });
          const url = URL.createObjectURL(typed);
          setBlobUrl(url);
        }
      } catch (err) {
        toast.error(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Önizleme yüklenemedi",
        );
      } finally {
        setLoadingBlob(false);
      }
    })();
  }, [file, unlocked]);

  if (!file) return null;

  const isAdmin = ["super_admin", "ultra_admin"].includes(user?.role);
  const isRecipient = (file.recipient_ids || []).includes(user?.id);
  const canReveal = file.has_password && (isAdmin || isRecipient);

  const verify = async () => {
    if (!password.trim()) return;
    setVerifying(true);
    try {
      await filesApi.verifyPassword(file.id, password);
      setUnlocked(true);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Şifre yanlış",
      );
    } finally {
      setVerifying(false);
    }
  };

  const revealPassword = async () => {
    setRevealing(true);
    try {
      const { password: pw } = await filesApi.revealPassword(file.id);
      setRevealedPw(pw || "");
      setPassword(pw || "");
      setShowPw(true);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Şifre gösterilemedi",
      );
    } finally {
      setRevealing(false);
    }
  };

  const download = async () => {
    try {
      const res = await api.get(filesApi.downloadUrl(file.id, password), {
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([res.data], {
          type: file.mime_type || "application/octet-stream",
        }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_name || file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "İndirme başarısız",
      );
    }
  };

  const Icon = iconFor(file.mime_type);
  const kind = previewKindFor(file.mime_type, file.size);

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-4xl overflow-hidden"
        data-testid="shared-file-preview-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            <Icon className="h-4 w-4 text-zinc-500" />
            <span className="truncate">{file.name}</span>
            {file.has_password && (
              <Lock className="h-3.5 w-3.5 text-amber-600" />
            )}
          </DialogTitle>
          <div className="text-[11px] text-zinc-400">
            {formatSize(file.size)} · {file.mime_type || "—"}
          </div>
        </DialogHeader>

        {!unlocked ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
                <Lock className="h-4 w-4" />
                Şifreli dosya
              </div>
              <p className="text-xs text-amber-800">
                Bu dosyayı görüntülemek için şifre gerekli.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Şifre</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && verify()}
                    autoFocus
                    data-testid="preview-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                  >
                    {showPw ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
                <Button
                  onClick={verify}
                  disabled={verifying || !password.trim()}
                  data-testid="preview-verify-btn"
                >
                  {verifying ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Aç"
                  )}
                </Button>
              </div>
              {canReveal && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={revealPassword}
                    disabled={revealing}
                    data-testid="preview-reveal-btn"
                    className="inline-flex items-center gap-1 rounded-md px-1 text-xs font-medium text-violet-700 hover:underline"
                  >
                    <Eye className="h-3 w-3" />
                    {revealing ? "Yükleniyor…" : "Şifreyi görüntüle"}
                  </button>
                  {revealedPw && (
                    <div className="mt-2 rounded-md bg-zinc-900 px-3 py-2 font-mono text-sm text-white">
                      {revealedPw}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="min-h-[300px] rounded-lg border border-zinc-200 bg-zinc-50 p-2">
              {loadingBlob ? (
                <div className="flex h-[300px] items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                </div>
              ) : kind === "image" && blobUrl ? (
                <img
                  src={blobUrl}
                  alt={file.name}
                  className="mx-auto max-h-[60vh] w-auto rounded"
                  data-testid="preview-image"
                />
              ) : kind === "pdf" && blobUrl ? (
                <iframe
                  src={blobUrl}
                  title={file.name}
                  className="h-[60vh] w-full rounded"
                  data-testid="preview-iframe"
                />
              ) : kind === "video" && blobUrl ? (
                <video
                  src={blobUrl}
                  controls
                  className="mx-auto max-h-[60vh] w-auto rounded"
                  data-testid="preview-video"
                />
              ) : kind === "audio" && blobUrl ? (
                <div className="flex h-[300px] items-center justify-center px-6">
                  <audio
                    src={blobUrl}
                    controls
                    className="w-full"
                    data-testid="preview-audio"
                  />
                </div>
              ) : kind === "text" ? (
                <pre
                  className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded bg-white p-3 font-mono text-xs text-zinc-800"
                  data-testid="preview-text"
                >
                  {textBody}
                </pre>
              ) : (
                <div className="flex h-[300px] flex-col items-center justify-center gap-3 text-sm text-zinc-500">
                  <Icon className="h-10 w-10 text-zinc-300" />
                  Bu dosya türü için önizleme yok.
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                <X className="mr-1 h-3.5 w-3.5" />
                Kapat
              </Button>
              <Button
                onClick={download}
                className="gap-2"
                data-testid="preview-download-btn"
              >
                <Download className="h-3.5 w-3.5" />
                İndir
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
